import { ContactCreateParams, Locale } from '@brenger/api-client';
import cn from 'classnames';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import { Col, InputText, Row } from '../../../../brenger-shared-ui';
import { InputPhone } from '../../../../brenger-shared-ui/components/form/InputPhone';
import { useTypedSelector } from '../../../../hooks';
import { getActiveLanguageSettings, translate } from '../../../../utils/localization';
import { validateEmail, validatePhone, validateRequired } from '../../../../utils/validation';
import { actions } from '../../ducks';
import { ContactType } from '../../typings';
import { isEqual } from 'lodash';
interface Props {
  forceShowErrors?: boolean;
  type: ContactType;
}

export const ContactFields: React.FC<Props> = ({ forceShowErrors, type }) => {
  const dispatch = useDispatch();
  const details = useTypedSelector(state => state.generalTransport.contact?.[type]?.contact_details);
  const detailsDelivery = useTypedSelector(state => state.generalTransport.contact?.delivery?.contact_details);
  const shouldUpdateDelivery = type === 'customer' && (!detailsDelivery || isEqual(details, detailsDelivery));
  const prefLocale = getActiveLanguageSettings().labels.full.replace('-', '_') as Locale;

  const onChange = (newDetails: Partial<ContactCreateParams>): void => {
    const payload = {
      ...(details || {}),
      ...newDetails,
      preferred_locale: prefLocale,
    };
    dispatch(
      actions.setContactDetails({
        contact_type: type,
        details: payload as ContactCreateParams,
      })
    );
    if (shouldUpdateDelivery) {
      dispatch(
        actions.setContactDetails({
          contact_type: 'delivery',
          details: payload as ContactCreateParams,
        })
      );
    }
  };

  return (
    <>
      <Row>
        <Col xs={12} sm={6} extraClasses="pb-[1em]">
          <InputText
            autoComplete="given-name"
            name="first_name"
            placeholder={translate('form.fields.user.first_name.label')}
            value={details?.first_name || ''}
            onChange={first_name => onChange({ first_name })}
            meta={{
              error: validateRequired(translate('form.fields.user.first_name.label'), details?.first_name),
              showError: forceShowErrors,
            }}
          />
        </Col>
        <Col xs={12} sm={6} extraClasses="pb-[1em]">
          <InputText
            autoComplete="family-name"
            name="last_name"
            placeholder={translate('form.fields.user.last_name.label')}
            value={details?.last_name || ''}
            onChange={last_name => onChange({ last_name })}
            meta={
              type === 'customer'
                ? {
                    error: validateRequired(translate('form.fields.user.last_name.label'), details?.last_name),
                    showError: forceShowErrors,
                  }
                : undefined
            }
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12} sm={6} extraClasses={cn('pb-[1em] md:pb-0')}>
          <InputText
            autoComplete="email"
            name="email"
            placeholder={translate('form.fields.user.email.label')}
            value={details?.email || ''}
            onChange={email => onChange({ email })}
            meta={{ error: validateEmail(details?.email), showError: forceShowErrors }}
          />
        </Col>
        <Col xs={12} sm={6}>
          <InputPhone
            autoComplete="tel"
            name="phone"
            defaultCountryCode={getActiveLanguageSettings().labels.short}
            placeholder={translate('form.fields.user.phone.label')}
            value={details?.phone || ''}
            onChange={phone => onChange({ phone })}
            meta={{ error: validatePhone(details?.phone), showError: forceShowErrors }}
          />
        </Col>
      </Row>
    </>
  );
};
